import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { ContributorsAutocomplete } from './contributors-search'
import { delay, escapeHtml, unescapeHtml } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-contributors-search-field', {
  title: 'Nom du contributeur*',
  placeholder: 'Écrivez ici...',
  choose: 'Choisir',
  addContributor: 'Ajouter un contributeur',
})

Locale.getInstance().registerKeySet('en', 'lvc-contributors-search-field', {
  title: 'Name of contributor*',
  placeholder: 'Type here...',
  choose: 'Choose',
  addContributor: 'Add contributor',
})

export interface LvcContributorsSearchField {
  title: string
  placeholder: string
  defaultInputText: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
  disableAddButton: boolean
}

export class LvcContributorsSearchField extends MinzeElement {
  private contributors: any[] = []
  private selectedContributor: String = ''
  private filter: string = ''
  attrs: Attrs = [
    'title',
    'placeholder',
    'default-input-text',
    'error',
    'disable-add-button',
    ['default-value', ''],
    ['on-change', ''],
  ]

  reactive: Reactive = [
    ['value', ''],
    ['tooltipOpened', false],
  ]

  tooltipElement: HTMLElement | null = null

  static observedAttributes = ['title', 'on-change', 'error', 'default-value']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  async onReady() {
    this.tooltipElement = this.select('#contributors-tooltip')

    const list = (await ContributorsAutocomplete.instance.getSuggestions(this.filter)) as any[]
    this.contributors = list || []
    // change the contributors object "contributorName" to "contributorName"
    await this.addContributorNameProperty()

    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value' && oldValue !== newValue) {
      this.updateDefaultValue()
    }
  }

  updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedContributor = (
        JSON.parse(decodeURIComponent(this.defaultValue)) as any
      ).contributorName
    }
    this.rerender()
  }

  async addContributorNameProperty() {
    this.contributors = this.contributors.map(contributor => {
      return {
        contributorName: Locale.language === 'en' ? contributor.nameEn : contributor.nameFr,
        ...contributor,
      }
    })
  }

  async onToggle() {
    this.open = !this.open
    const filterElement = this.select('input[name="filter"]') as HTMLInputElement
    this.rerender()
    // Need to wait for the rerender to complete before focusing the input otherwise it won't work
    await delay(0)
    filterElement.focus()
  }

  onContributorClick(e: Event): void {
    if (!e.target || !(e.target instanceof HTMLElement)) return

    let clickedContributor = e.target.getAttribute('data-id')
    if (!clickedContributor) return

    // Unescape the contributor name since it was escaped in the data-id attribute
    clickedContributor = unescapeHtml(clickedContributor)

    if (this.selectedContributor === clickedContributor) {
      this.selectedContributor = ''
    } else {
      this.selectedContributor = clickedContributor
    }

    this.serializeValue()
    this.onToggle()
    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    const list = (await ContributorsAutocomplete.instance.getSuggestions(this.filter)) as any[]
    this.contributors = list || []
    await this.addContributorNameProperty()
    this.rerender()
  }

  addContributor() {
    Callback.call(this.onChange, { value: this.filter, element: this })
    this.selectedContributor = this.filter
    this.cast('change', { value: this.filter, element: this })
    this.onToggle()
  }

  serializeValue() {
    let objects = this.contributors.filter(
      k => this.selectedContributor.indexOf(k.contributorName) > -1
    )

    this.value = encodeURIComponent(JSON.stringify(objects))
    console.log('serialize value called with', objects)
    Callback.call(this.onChange, { value: objects, element: this })
    this.cast('change', { value: objects, element: this }) // not sure this does anything.
  }

  getContributorsLabel() {
    if (!this.selectedContributor) {
      return this.defaultInputText || Locale.getKey('lvc-contributors-search-field.choose')
    }

    // if the user entered the contributor name manually, return the filter value
    if (
      this.selectedContributor &&
      this.contributors.findIndex(
        contributor => `${contributor.contributorName}` === this.selectedContributor
      ) === -1
    ) {
      return this.selectedContributor
    }

    const selectedContributor = this.contributors.find(
      contributor => `${contributor.contributorName}` === this.selectedContributor
    )
    return selectedContributor ? selectedContributor.contributorName : ''
  }

  renderContributor = (contributor: any) => {
    if (!contributor || !contributor.contributorName) {
      return ''
    }

    let isNotSelected = this.selectedContributor.indexOf(`${contributor.contributorName}`) == -1

    return `
      <button 
        name="contributor"
        class="${isNotSelected ? 'not-selected' : 'selected'}"
        data-id="${escapeHtml(contributor.contributorName)}">
        ${Locale.language === 'en' ? escapeHtml(contributor.nameEn) : escapeHtml(contributor.contributorName)}
        ${isNotSelected ? '' : '<lvc-circle-check-select-icon></lvc-circle-check-select-icon>'}
      </button>
    `
  }

  renderContributors = () => {
    let html = ''

    for (let i = 0; i < this.contributors.length; i++) {
      html += this.renderContributor(this.contributors[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0
    return /*html*/ `
      <div class="title">${this.title || Locale.getKey('lvc-contributors-search-field.title')}
      </div>
      <div class="input ${this.selectedContributor?.length || (this.defaultValue && this.defaultValue !== true) ? 'has-value' : ''}  ${this.error ? 'error' : ''}">
        <button class="dropbtn" name="toggle">
          <lvc-search-icon></lvc-search-icon>
          ${this.getContributorsLabel()}
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
          <lvc-search-icon></lvc-search-icon>
            <input name="filter" type="text" value="${escapeHtml(this.filter)}" placeholder="${this.placeholder || Locale.getKey('lvc-contributors-search-field.placeholder')}"/>
          </div>
          <div class="contributors">
            ${this.renderContributors()}
          </div>
          ${
            this.disableAddButton
              ? ''
              : `
          <div class="footer">
            <button class="addContributor">
              <lvc-add-icon></lvc-add-icon>
              ${Locale.getKey('lvc-contributors-search-field.addContributor')}
            </button>
          </div>
          `
          }
        </div>
      </div>
      ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
    `
  }

  css = () => {
    return `
      :host {
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .footer {
        display: flex;
        height: 60px;
        padding: 0 10px;
      }

      .tooltip-dialog {
        display: none;
        position: absolute;
        background-color: #fff;
        border: 1px solid #ccc;
        padding: 10px;
        z-index: 3; /* Ensure the dialog appears above other elements */
        /* Add any other styles for the dialog */
    }

      lvc-add-icon {
        display: flex;
        align-items: center;
      }

      .addContributor {
        color: #ffff;
        background: var(--Minuit, #1E007D);
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%; 
        display: flex;
        padding: 10px 16px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex: 1 1 0;
        border: none;
        margin: 10px;
        cursor: pointer;
        border-radius: 999px;
      }

      .header {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 8px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: #BFF0FF;

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;
        display: flex;
        

        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 3.5px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 60px;
      }
      
      .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }

      .contributors {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .contributors button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }



      .contributors button:last-child {
        border-bottom: none;
      }

      .contributors button.selected {
      }

      .contributors-tooltip-content ul {
        margin: 0;
        padding-left: 15px;
        list-style: none; /* Remove default list style */
      }
  
      .contributors-tooltip-content li {
        font-size: 14px;
        font-weight: 500;
        line-height: 21px;
        letter-spacing: 0px;
        text-align: left;
        
      }
      
      .contributors-tooltip-content li::before {
        content: ''; /* Empty content to remove default bullet point */
        display: inline-block;
        width: 9px;
        background-color: red;
        height: 10px;
        margin-right: 8px;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10" fill="none" stroke="%231E007D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 9C6.72234 7.77224 5.49206 6.47963 4.31317 5.1268M4.31317 5.1268C3.16768 3.81229 2.04474 2.42401 1 1M4.31317 5.1268C5.39654 3.6513 6.68118 2.41964 8 1M4.31317 5.1268C3.06821 6.63983 2.15598 7.52691 1 9" /></svg>') no-repeat center center;
      }

      .contributors-tooltip-content h3 {
        margin: 0 0 8px;
      }
      
      .contributors-tooltip-content ul {
        margin: 0;
        padding-left: 15px;
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 12px 8px;
        font-size: 14px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }
      
      .dropbtn span {
        color: rgba(30, 0, 125, 0.5);
      }
      
      .input.has-value .dropbtn span {
        color: rgba(30, 0, 125, 1);
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      lvc-search-icon {
        margin-right: 8px;
        display:flex;
        align-items: center;
      }

      lvc-help-icon {
        display:flex;
        align-items: center;
        margin-left: 4px;
        cursor: pointer;
      }

      /* Dropdown Content */
      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #ffff;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }

      .close-overlay {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1;
      }

      .contributors::-webkit-scrollbar {
        display: none;
      }

      .error {
        background: #FFEBD7;
      }
    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="contributor"]', 'click', this.onContributorClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
    ['.addContributor', 'click', this.addContributor.bind(this)],
  ]
}
